export type WindowsBitness = "64" | "32" | "unknown";

interface UserAgentHighEntropyValues {
  architecture?: string;
  bitness?: string;
  wow64?: boolean;
}

interface UserAgentDataLike {
  platform?: string;
  getHighEntropyValues?: (
    hints: string[]
  ) => Promise<UserAgentHighEntropyValues>;
}

function isWindowsEnvironment(): boolean {
  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const currentUserAgentData = (navigator as any).userAgentData;
  const userAgentPlatform =
    currentUserAgentData && currentUserAgentData.platform
      ? currentUserAgentData.platform
      : "";

  return /Windows/i.test(
    `${userAgentPlatform} ${platform} ${userAgent}`
  );
}

/**
 * 判断 Windows 操作系统位数。
 *
 * 仅使用 UA-CH 明确返回的信息。现代 Chromium 的缩减 UA 会把 Windows
 * 统一写成 Win64/x64，即使真实系统是 32 位，因此不能回退到 UA 判断。
 * 无法可靠判断时返回 unknown，交给用户手动选择。
 */
export async function detectWindowsBitness(): Promise<WindowsBitness> {
  if (!isWindowsEnvironment()) {
    return "unknown";
  }

  const userAgentData = (navigator as any).userAgentData as
    | UserAgentDataLike
    | undefined;

  if (
    !userAgentData ||
    typeof userAgentData.getHighEntropyValues !== "function"
  ) {
    return "unknown";
  }

  try {
    const values = await userAgentData.getHighEntropyValues([
      "architecture",
      "bitness",
      "wow64",
    ]);

    const architecture = values && values.architecture;
    const bitness = values && values.bitness;
    const wow64 = values && values.wow64;

    // wow64=true 表示 32 位浏览器运行在 64 位 Windows 上。
    if (bitness === "64" || wow64 === true) {
      return "64";
    }

    // 32 位系统要求三项信息都明确返回，不能把缺失的 wow64 当作 false。
    if (
      architecture === "x86" &&
      bitness === "32" &&
      wow64 === false
    ) {
      return "32";
    }

    return "unknown";
  } catch (error) {
    return "unknown";
  }
}
